import {
  IconButton,
  Link,
  LinkProps,
  Stack,
  StackProps,
  Wrap,
  WrapItem,
  WrapItemProps,
  WrapProps
} from "@chakra-ui/react";
import {
  AiFillFacebook,
  AiFillInstagram,
  AiFillLinkedin,
  AiFillYoutube,
  AiOutlineGoogle
} from "react-icons/ai";
import { FaXTwitter } from "react-icons/fa6";

export const FooterSocialMediaLinks = ({
  facebookUrl,
  twitterUrl,
  googleUrl,
  linkedinUrl,
  instagramUrl,
  youtubeUrl,
  className,
  order,
  id
}: {
  facebookUrl?: string;
  twitterUrl?: string;
  googleUrl?: string;
  linkedinUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
} & Pick<StackProps, "order" | "className" | "id">): JSX.Element => {
  return (
    <Stack
      spacing={2}
      direction="row"
      className={className}
      order={order}
      id={id}
    >
      {[
        {
          link: facebookUrl,
          icon: <AiFillFacebook />,
          label: "facebook"
        },
        {
          link: twitterUrl,
          icon: <FaXTwitter />,
          label: "twitter"
        },
        {
          link: googleUrl,
          icon: <AiOutlineGoogle />,
          label: "google"
        },
        {
          link: linkedinUrl,
          icon: <AiFillLinkedin />,
          label: "linkedin"
        },
        {
          link: instagramUrl,
          icon: <AiFillInstagram />,
          label: "instagram"
        },
        {
          link: youtubeUrl,
          icon: <AiFillYoutube />,
          label: "youtube"
        }
      ].map(({ link, icon, label }, index) => {
        if (!link) return null;

        return (
          <IconButton
            h="auto"
            minW="auto"
            bg="inherit"
            as={Link}
            key={index}
            icon={icon}
            href={link}
            className="ehq-participant-footer__social-links__link"
            variant="unstyled"
            aria-label={label}
          />
        );
      })}
    </Stack>
  );
};

export const FooterLink = ({
  className,
  children,
  id
}: Pick<WrapItemProps, "children" | "className" | "id">): JSX.Element => {
  return (
    <WrapItem
      alignItems="center"
      cursor="pointer"
      id={id}
      className={className}
    >
      {children}
    </WrapItem>
  );
};

export const FooterLinks = ({
  className,
  children,
  order,
  id
}: Pick<WrapProps, "children" | "order" | "className" | "id">): JSX.Element => {
  return (
    <Wrap
      mx={5}
      p={1}
      spacing={4}
      align="center"
      fontWeight={400}
      whiteSpace="nowrap"
      width={{ base: "100%", lg: "calc(100vw - 350px)" }}
      justify={{ base: "center", lg: "initial" }}
      className={className}
      order={order}
      id={id}
    >
      {children}
    </Wrap>
  );
};

export const FooterLogo = ({
  className,
  children,
  order,
  href,
  id
}: Pick<
  LinkProps,
  "children" | "order" | "className" | "id" | "href"
>): JSX.Element => {
  return (
    <Link
      maxW={150}
      isExternal
      fontWeight={600}
      textAlign="center"
      rel="noopener noreferrer"
      href={href || "https://granicus.com"}
      className={className}
      order={order}
      id={id}
    >
      {children}
    </Link>
  );
};

export const Footer = ({
  className,
  boxShadow,
  fontSize,
  background,
  children,
  color,
  id,
  position
}: Pick<
  StackProps,
  | "children"
  | "color"
  | "background"
  | "fontSize"
  | "boxShadow"
  | "className"
  | "id"
  | "position"
>): JSX.Element => {
  return (
    <Stack
      px={5}
      py={4}
      wrap="wrap"
      align="center"
      justify="center"
      spacing={{ base: 4, lg: "auto" }}
      direction={{ base: "column", lg: "row" }}
      justifyContent={{ base: "center", lg: "space-between" }}
      background={background || "black"}
      boxShadow={boxShadow || "sm"}
      fontSize={fontSize || "sm"}
      color={color || "white"}
      className={className}
      id={id}
      width="100%"
      position={position}
    >
      {children}
    </Stack>
  );
};

export default Footer;
